import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "sap_invoice_filters_panel_collapsed"

// Filter panel: collapse/expand (localStorage), date presets, From <= To validation, disable Apply when invalid.
export default class extends Controller {
  static targets = ["fromDate", "toDate", "hiddenFromDate", "hiddenToDate", "rangeToggle", "rangeWrapper", "errorMessage", "applyButton", "body", "toggleBtn"]
  static values = { storageKey: { type: String, default: STORAGE_KEY } }

  connect() {
    this.updateRangeMode()
    this.syncHiddenDates()
    this.restoreCollapsed()
    this.checkValidity()
    // Some browsers restore checkbox state after connect; resync on next tick.
    setTimeout(() => {
      this.updateRangeMode()
      this.syncHiddenDates()
      this.checkValidity()
    }, 0)
  }

  toggleRange() {
    this.updateRangeMode()
    this.syncHiddenDates()
    this.checkValidity()
  }

  // --- Collapse / Expand ---
  toggle(event) {
    event.preventDefault()
    const wrapper = this.element.hasAttribute("data-sap-invoice-filters-wrapper") ? this.element : this.element.closest("[data-sap-invoice-filters-wrapper]")
    if (!wrapper) return
    const isCollapsed = wrapper.classList.toggle("sap-invoice-filters--collapsed")
    if (this.hasBodyTarget) this.bodyTarget.classList.toggle("hidden", isCollapsed)
    if (this.hasToggleBtnTarget) {
      this.updateToggleLabel(isCollapsed)
      this.toggleBtnTarget.setAttribute("aria-expanded", !isCollapsed)
    }
    try {
      localStorage.setItem(this.storageKeyValue, isCollapsed ? "1" : "0")
    } catch (_) {}
  }

  restoreCollapsed() {
    const wrapper = this.element.hasAttribute("data-sap-invoice-filters-wrapper") ? this.element : this.element.closest("[data-sap-invoice-filters-wrapper]")
    if (!wrapper) return
    try {
      const stored = localStorage.getItem(this.storageKeyValue)
      if (stored === "1") {
        wrapper.classList.add("sap-invoice-filters--collapsed")
        if (this.hasBodyTarget) this.bodyTarget.classList.add("hidden")
        if (this.hasToggleBtnTarget) {
        this.updateToggleLabel(true)
        this.toggleBtnTarget.setAttribute("aria-expanded", "false")
      }
      }
    } catch (_) {}
  }

  updateToggleLabel(collapsed) {
    const label = this.toggleBtnTarget.querySelector("[data-toggle-label]")
    const icon = this.toggleBtnTarget.querySelector("[data-toggle-icon]")
    if (label) label.textContent = collapsed ? this.toggleBtnTarget.dataset.expandText || "Expand" : this.toggleBtnTarget.dataset.collapseText || "Collapse"
    if (icon) icon.classList.toggle("rotate-180", !collapsed)
  }

  syncHiddenDates() {
    if (!this.hasFromDateTarget || !this.hasToDateTarget) return
    if (this.hasHiddenFromDateTarget) this.hiddenFromDateTarget.value = this.monthStart(this.fromDateTarget.value)
    const toMonth = this.isRangeMode() ? this.toDateTarget.value : this.fromDateTarget.value
    if (this.hasHiddenToDateTarget) this.hiddenToDateTarget.value = this.monthEnd(toMonth)
  }

  // --- Validation: From <= To, disable Apply, show error ---
  checkValidity() {
    this.syncHiddenDates()
    const from = this.hasFromDateTarget && this.fromDateTarget.value ? new Date(`${this.fromDateTarget.value}-01`) : null
    const to = this.hasToDateTarget && this.toDateTarget.value ? new Date(`${this.toDateTarget.value}-01`) : null
    const boundsInvalid = (this.hasFromDateTarget && !this.fromDateTarget.checkValidity()) || (this.isRangeMode() && this.hasToDateTarget && !this.toDateTarget.checkValidity())
    const invalid = boundsInvalid || (this.isRangeMode() && from && to && from > to)
    if (invalid) {
      this.showError()
      if (this.hasApplyButtonTarget) this.applyButtonTarget.disabled = true
    } else {
      this.hideError()
      if (this.hasApplyButtonTarget) this.applyButtonTarget.disabled = false
    }
  }

  validate(event) {
    this.syncHiddenDates()
    const from = this.fromDateTarget.value ? new Date(`${this.fromDateTarget.value}-01`) : null
    const to = this.toDateTarget.value ? new Date(`${this.toDateTarget.value}-01`) : null
    const boundsInvalid = (this.hasFromDateTarget && !this.fromDateTarget.checkValidity()) || (this.isRangeMode() && this.hasToDateTarget && !this.toDateTarget.checkValidity())
    if (boundsInvalid) {
      event.preventDefault()
      this.showError()
      return
    }
    if (!from || (this.isRangeMode() && !to)) {
      this.hideError()
      return
    }
    if (this.isRangeMode() && from > to) {
      event.preventDefault()
      this.showError()
      return
    }
    this.hideError()
  }

  isRangeMode() {
    return this.hasRangeToggleTarget && this.rangeToggleTarget.checked
  }

  updateRangeMode() {
    if (!this.hasRangeWrapperTarget) return
    const enabled = this.isRangeMode()
    this.rangeWrapperTarget.classList.toggle("hidden", !enabled)
    if (this.hasToDateTarget) this.toDateTarget.disabled = !enabled
  }

  showError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
      this.errorMessageTarget.setAttribute("aria-live", "polite")
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  monthStart(monthValue) {
    if (!monthValue) return ""
    return `${monthValue}-01`
  }

  monthEnd(monthValue) {
    if (!monthValue) return ""
    const [yearStr, monthStr] = monthValue.split("-")
    const year = Number(yearStr)
    const month = Number(monthStr)
    if (!year || !month) return ""
    const lastDay = new Date(year, month, 0).getDate()
    return `${yearStr}-${monthStr}-${String(lastDay).padStart(2, "0")}`
  }
}
